/* eslint-disable no-unused-vars, react/require-default-props */
import React, { PropsWithoutRef, RefAttributes } from 'react';
import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

type IWithCustomClassNameInnerProps<P extends object, T> = P & {
  className?: string;
  forwardedRef?: React.Ref<T>;
  bsRole?: string; // Make optional for flexibility
};

export function withCustomClassName<P extends object>(
  WrappedComponent: React.ComponentType<P & { className?: string }>,
  customClassName: string,
  bsRole: string = ''
): React.ForwardRefExoticComponent<
  PropsWithoutRef<P> & { className?: string; bsRole?: string } & RefAttributes<React.ComponentRef<typeof WrappedComponent>>
  > {
  type WrappedComponentInstance = React.ComponentRef<typeof WrappedComponent>;
  const WrappedComponentWithCustomClassName: React.FC<IWithCustomClassNameInnerProps<P, WrappedComponentInstance>> = (props) => {
    const {
      // eslint-disable-next-line no-unused-vars
      bsRole: role,
      className = '',
      forwardedRef = null,
      ...rest
    } = props;

    const nameProp = classNames(className, customClassName);

    return (
      <WrappedComponent
        className={nameProp}
        ref={forwardedRef}
        {...(rest as P)}
      />
    );
  };

  WrappedComponentWithCustomClassName.displayName = `withCustomClassName(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(WrappedComponentWithCustomClassName, WrappedComponent);

  const WrappedComponentWithForwardRef = React.forwardRef<WrappedComponentInstance, PropsWithoutRef<P> & { className?: string; bsRole?: string }>((props, ref) => (
    <WrappedComponentWithCustomClassName
      forwardedRef={ref}
      {...(props as IWithCustomClassNameInnerProps<P, WrappedComponentInstance>)}
    />
  ));

  (WrappedComponentWithForwardRef as any).defaultProps = {
    bsRole,
    className: undefined,
  };

  return WrappedComponentWithForwardRef as React.ForwardRefExoticComponent<
  PropsWithoutRef<P> & { className?: string; bsRole?: string } & RefAttributes<WrappedComponentInstance>
  >;
}
